import { useTranslation } from "react-i18next";
import { useMemo, type SVGProps } from "react";
import { Bar, BarChart, XAxis, YAxis, type LabelProps } from "recharts";
import useQuestionResponse from "../../../Responses/useQuestionResponse";
import { ChartContainer } from "frontend-components";

function CustomTick(props: SVGProps<SVGTextElement> & { payload?: { value: string } }) {
    const { x, y, payload } = props;
    return (
        <g>
            <rect
                x={+(x || 0) - 12}
                y={+(y || 0) - 13}
                width={25}
                height={25}
                fill="var(--color-secondary)"
            />
            <text
                textAnchor="middle"
                dominantBaseline="middle"
                fill="var(--color-on-secondary)"
                x={+(x || 0) + 0.5}
                y={+(y || 0) - 0.5}
            >
                {payload?.value}
            </text>
        </g>
    );
}

function CustomLabel(props: LabelProps) {
    const { y, value } = props;
    const { i18n } = useTranslation();

    return (
        <text
            x="100%"
            dx={-40}
            y={+(y || 0) + 4} // adjust for bar height
            textAnchor="start"
            dominantBaseline="middle"
            fill="var(--color-on-surface)"
        >
            {value?.toLocaleString(i18n.language, { maximumFractionDigits: 0 })}%
        </text>
    );
}

export default function RatingChart() {
    const { t } = useTranslation();
    const { answers } = useQuestionResponse();

    const chartData = useMemo(() => {
        const allowedAnswers = ["1", "2", "3", "4", "5"];
        const counts: Record<string, number> = Object.fromEntries(
            allowedAnswers.map((a) => [a, 0]),
        );
        let total = 0;
        for (const ans of answers) {
            if (allowedAnswers.includes(String(ans.answerValue))) {
                counts[String(ans.answerValue)]++;
                total++;
            }
        }

        return allowedAnswers.map((answer) => ({
            answer,
            avg: counts[answer] && total ? (counts[answer] / total) * 100 : 0, // Convert to percentage
            fill: "var(--color-surface-teal-dark)",
        }));
    }, [answers]);

    const chartConfig = {
        avg: {
            label: t("common.average"),
        },
        "1": {
            label: "1",
        },
        "2": {
            label: "2",
        },
        "3": {
            label: "3",
        },
        "4": {
            label: "4",
        },
        "5": {
            label: "5",
        },
    };

    return (
        <ChartContainer config={chartConfig} className="h-44 w-full -ml-3 mt-4">
            <BarChart
                accessibilityLayer
                data={chartData}
                layout="vertical"
                margin={{ left: -20, right: 44 }}
            >
                <XAxis
                    type="number"
                    dataKey={"avg"}
                    allowDecimals={false}
                    tickLine={false}
                    tick={false}
                    axisLine={false}
                    domain={[
                        0,
                        (dataMax: number) => {
                            const buffered = Math.ceil(dataMax * 1.15);
                            return buffered > 100 ? 100 : buffered;
                        },
                    ]}
                />
                <YAxis
                    type="category"
                    dataKey={"answer"}
                    tickMargin={10}
                    tickCount={2}
                    tick={<CustomTick />}
                    interval={0}
                    tickLine={false}
                    axisLine={false}
                />
                <Bar
                    dataKey="avg"
                    fill="var(--color-surface-teal-dark)"
                    barSize={8}
                    radius={4}
                    unit={"%"}
                    background={{ fill: "var(--color-compendia-greige-60)", radius: 4 }}
                    label={<CustomLabel />}
                    isAnimationActive={false}
                />
            </BarChart>
        </ChartContainer>
    );
}
